import { formatDistanceToNowStrict, isValid, Locale, parseISO } from 'date-fns'
import * as locales from 'date-fns/locale'
import i18n from 'i18next'
import { isDate } from 'lodash-es'

import { isString } from '@/utils/isString'

/**
 * Formats a point in time as a localized distance to now, for example
 * "2 hours ago" or "vor 2 Stunden".
 *
 * Uses the strict variant on purpose: it keeps the largest unit only and does
 * not round up to "about", which matches the timeline design ("30 seconds ago",
 * "33 minutes ago", "1 day ago").
 *
 * @param time Date object or ISO date string.
 * @returns The localized relative time, or an empty string when the input is
 * missing, of an unsupported type, or not a valid date.
 */
export const formatTimeAgo = (time: unknown): string => {
  const date = toDate(time)
  if (!date) return ''

  return formatDistanceToNowStrict(date, {
    addSuffix: true,
    locale: getLocale(),
  })
}

const toDate = (time: unknown): Date | undefined => {
  if (isDate(time)) {
    return isValid(time) ? time : undefined
  }

  if (isString(time) && time) {
    const parsed = parseISO(time)
    return isValid(parsed) ? parsed : undefined
  }

  return undefined
}

const getLocale = () => {
  const language = i18n.resolvedLanguage ?? i18n.language ?? ''
  return (locales as { [key: string]: Locale })[language]
}
